import * as THREE from 'three';
import { createRenderers } from './scene/renderers';
import { OrbitControls } from './scene/camera-controls';
import { createPanel, createRegionLabel, type PanelHandle } from './scene/panels';
import { ConnectionLayer } from './scene/connections';
import { HoverManager } from './interactions/hover';
import { DetailPanel } from './interactions/detail';
import { FilterManager } from './interactions/filters';
import { ALL_ELEMENTS, REGION_LABELS, FEATURE } from './data/system';

function main() {
  const viewport = document.getElementById('viewport')!;
  const { scene, cssScene, camera, webgl, css3d } = createRenderers(viewport);

  // Quiet horizon rules — anchor the composition without competing with content.
  const horizonMat = new THREE.LineBasicMaterial({
    color: 0xc9bea4, transparent: true, opacity: 0.35, depthWrite: false,
  });
  const horizonGeo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-14, -5.6, -8),
    new THREE.Vector3(14, -5.6, -8),
  ]);
  scene.add(new THREE.Line(horizonGeo, horizonMat));
  const horizonGeo2 = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-13, 7.4, -8),
    new THREE.Vector3(13, 7.4, -8),
  ]);
  scene.add(new THREE.Line(horizonGeo2, horizonMat.clone()));

  // ─── Panels ────────────────────────────────────────────────
  const panels: PanelHandle[] = ALL_ELEMENTS.map(createPanel);
  for (const p of panels) cssScene.add(p.cssObj);

  // ─── Region labels ─────────────────────────────────────────
  for (const lbl of REGION_LABELS) cssScene.add(createRegionLabel(lbl));

  // ─── Camera + controls ─────────────────────────────────────
  const target = new THREE.Vector3(...FEATURE.position);
  const controls = new OrbitControls({ domElement: viewport, camera, target });
  // Debug hook for headless smoke testing — harmless in prod.
  (window as unknown as { __controls?: unknown }).__controls = controls;


  // ─── Connections + hover ───────────────────────────────────
  const connections = new ConnectionLayer(scene);
  const hover = new HoverManager(panels, connections);
  void hover;

  // ─── Detail panel ──────────────────────────────────────────
  const detail = new DetailPanel('#detail-root');
  detail.onTagClick = (id) => detail.open(id);
  for (const p of panels) {
    p.domElement.addEventListener('click', (e) => {
      e.stopPropagation();
      detail.open(p.id);
    });
  }

  // ─── Filters ───────────────────────────────────────────────
  const legendEl = document.getElementById('legend')!;
  const clearBtn = document.getElementById('clear-filters')!;
  const filters = new FilterManager(panels, legendEl, clearBtn);
  void filters;

  // ─── Reset-view affordance ─────────────────────────────────
  const resetBtn = document.getElementById('reset-view') as HTMLButtonElement;
  // Stop pointerdown bubbling to OrbitControls (setPointerCapture would
  // otherwise redirect the mouseup and kill the click event).
  resetBtn.addEventListener('pointerdown', (e) => e.stopPropagation());
  resetBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    controls.returnHome();
  });

  // Show the reset button only when the camera is meaningfully off-home.
  let resetVisible = false;
  function syncResetVisibility() {
    const away = controls.isAwayFromHome();
    if (away !== resetVisible) {
      resetVisible = away;
      resetBtn.classList.toggle('visible', away);
    }
  }

  // Double-click on empty space → return home.
  viewport.addEventListener('dblclick', (e) => {
    if ((e.target as HTMLElement)?.closest('.panel, .legend, .reset-view')) return;
    controls.returnHome();
  });

  // (No transient hint anymore — the controls panel is a persistent
  // reference rather than a first-use prompt.)

  // ─── Loading transition ────────────────────────────────────
  const loading = document.getElementById('loading');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => loading?.classList.add('hidden'));
  });
  setTimeout(() => loading?.parentElement?.removeChild(loading!), 900);

  // ─── Render loop ───────────────────────────────────────────
  function frame() {
    controls.update();
    connections.update();
    syncResetVisibility();
    webgl.render(scene, camera);
    css3d.render(cssScene, camera);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}

main();
